import { message } from 'antd';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import {
  DELETE_CONFIRM,
  DELETE_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServicesService } from 'services/ServicesService';
import { formatDate } from 'utils';
import { priceRender } from 'utils/table';
import { PackageTable } from './PackageTable';

function Package() {
  useDocumentTitle('Все услуги');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
  } = useDrawers();
  const { setCount } = useHeaderChanger('Все услуги');
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  const recordFields = useMemo(() => {
    return [
      { label: 'Наименование', key: 'title' },
      { label: 'Наименование (каз)', key: 'title_kz' },
      { label: 'Краткое описание', key: 'short_description' },
      { label: 'Краткое описание (каз)', key: 'short_description_kz' },
      {
        label: 'Тип',
        key: 'type',
        computedValue: activeRecord?.type === 'package' ? 'Пакет' : 'Услуга',
      },
      {
        label: 'Цена',
        key: 'price',
        computedValue: `${priceRender(activeRecord?.price)} ₸`,
      },
      {
        label: 'Дата создания',
        key: 'created',
        computedValue: formatDate(activeRecord?.created),
      },
      { label: 'Скидка (%)', key: 'discount_percent' },
      {
        label: 'Активный',
        key: 'is_public',
        computedValue: activeRecord?.is_public ? 'Да' : 'Нет',
      },
      {
        label: 'С бухгалтером',
        key: 'with_accountant',
        computedValue: activeRecord?.with_accountant ? 'Да' : 'Нет',
      },
    ];
  }, [activeRecord]);

  const fetchPackages = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ServicesService.getPackages({
        page,
        perPage,
      });

      setItems(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error('Не удалось загрузить пакеты');
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => fetchPackages(), [fetchPackages]);

  const navigateToEdit = () => {
    navigate(`/setting/services/edit/${activeRecord?.id}`);
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await ServicesService.deletePackage(activeRecord?.id);

      if (isDeleted) {
        closeDrawer();
        fetchPackages();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader
        onCreate={() => navigate('/setting/services/create')}
        pageId="package_service"
      />

      <PackageTable
        items={items}
        pageSize={perPage}
        current={page}
        loading={loading}
        onRowClick={openDrawer}
        total={totalRows}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        pageId="package_service"
        recordFields={recordFields}
        drawerTitle={activeRecord?.title}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={navigateToEdit}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
      />
    </>
  );
}

export { Package };
